<template>
  <div
    class="overflow-hidden rounded-3xl border border-gray-100 bg-white/80 shadow-[0_18px_36px_rgba(15,23,42,0.08)] backdrop-blur-sm"
  >
    <div
      v-if="$slots.filters"
      class="border-b border-gray-100 bg-gray-50/60 px-4 py-4 sm:px-6"
    >
      <slot name="filters" />
    </div>

    <!-- Desktop Table View -->
    <div class="hidden md:block relative overflow-x-auto">
      <table class="min-w-full table-fixed">
        <colgroup>
          <col
            v-for="col in columns"
            :key="col.key"
            :style="getColumnStyle(col)"
          />
        </colgroup>
        <thead class="bg-gray-50/70">
          <tr class="divide-x divide-transparent">
            <th
              v-for="column in columns"
              :key="column.key"
              :class="[
                'px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 sm:px-6 sm:py-4',
                column.headerClass,
              ]"
              scope="col"
            >
              <SortableHeader
                v-if="column.sortable"
                :field="column.key"
                :label="column.label"
                :sort-direction="sortDirection"
                :sort-field="sortField"
                align="left"
                @sort="handleSort"
              />
              <span v-else class="block w-full">
                {{ column.label }}
              </span>
            </th>
          </tr>
        </thead>
        <tbody class="relative divide-y divide-gray-100 bg-white">
          <LoadingRows
            v-if="loading"
            :columns="columns"
            :page-size="pageSize"
          />

          <template v-else>
            <tr v-if="data.length === 0" class="transition">
              <td
                :colspan="columns.length"
                class="px-6 py-16 text-center text-sm text-gray-500"
              >
                No records to display.
              </td>
            </tr>
            <tr
              v-for="item in data"
              :key="getItemId(item)"
              class="transition hover:bg-gray-50/70"
            >
              <td
                v-for="column in columns"
                :key="column.key"
                :class="[
                  'px-4 py-4 align-middle text-sm text-gray-700 sm:px-6 text-left',
                  column.cellClass,
                ]"
              >
                <slot
                  :item="item"
                  :name="`cell-${column.key}`"
                  :value="item[column.key]"
                >
                  {{ formatCellValue(item, column) }}
                </slot>
              </td>
            </tr>
          </template>

          <LoadingSpinner v-if="loading" />
        </tbody>
      </table>
    </div>

    <!-- Mobile Card View -->
    <div class="md:hidden">
      <div v-if="loading" class="divide-y divide-gray-100">
        <LoadingRows :columns="columns" :page-size="pageSize" />
      </div>

      <div
        v-else-if="data.length === 0"
        class="px-4 py-16 text-center text-sm text-gray-500"
      >
        No records to display.
      </div>

      <div v-else class="divide-y divide-gray-100">
        <div
          v-for="item in data"
          :key="getItemId(item)"
          class="px-4 py-4 sm:px-6 transition hover:bg-gray-50/70"
        >
          <div
            v-for="column in columns"
            :key="column.key"
            :class="[
              'mb-3 last:mb-0',
              column.key === 'actions' ? 'flex justify-start gap-2' : '',
            ]"
          >
            <!-- Hide certain columns on mobile based on headerClass -->
            <div
              v-if="
                !column.headerClass ||
                !column.headerClass.includes('hidden md:')
              "
            >
              <div v-if="column.key !== 'actions'" class="flex flex-col gap-1">
                <span
                  class="text-xs font-semibold uppercase tracking-wide text-gray-500"
                >
                  {{ column.label }}
                </span>
                <slot
                  :item="item"
                  :name="`cell-${column.key}`"
                  :value="item[column.key]"
                >
                  <span class="text-sm text-gray-700">
                    {{ formatCellValue(item, column) }}
                  </span>
                </slot>
              </div>
              <div v-else class="flex items-center justify-start gap-2">
                <slot
                  :item="item"
                  :name="`cell-${column.key}`"
                  :value="item[column.key]"
                >
                  {{ formatCellValue(item, column) }}
                </slot>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <TablePagination
      v-if="showPagination"
      :current-page="currentPage"
      :page-size="pageSize"
      :page-size-options="pageSizeOptions"
      :total-items="totalItems"
      :total-pages="totalPages"
      @page-change="$emit('page-change', $event)"
      @page-size-change="$emit('page-size-change', $event)"
      @prev-page="$emit('prev-page')"
      @next-page="$emit('next-page')"
    />
  </div>
</template>

<script>
import SortableHeader from "./SortableHeader.vue";
import TablePagination from "./TablePagination.vue";
import LoadingRows from "./LoadingRows.vue";
import LoadingSpinner from "./LoadingSpinner.vue";

export default {
  name: "DataTable",
  components: {
    SortableHeader,
    TablePagination,
    LoadingRows,
    LoadingSpinner,
  },
  props: {
    data: { type: Array, default: () => [] },
    columns: { type: Array, required: true },
    loading: { type: Boolean, default: false },
    sortField: { type: String, default: null },
    sortDirection: { type: String, default: null },
    currentPage: { type: Number, default: 1 },
    totalPages: { type: Number, default: 1 },
    totalItems: { type: Number, default: 0 },
    pageSize: { type: Number, default: 10 },
    pageSizeOptions: { type: Array, default: () => [6, 10, 20, 50] },
    showPagination: { type: Boolean, default: true },
    itemIdField: { type: String, default: "id" },
  },
  emits: ["sort", "page-change", "page-size-change", "prev-page", "next-page"],
  methods: {
    handleSort(field) {
      this.$emit("sort", field);
    },
    getItemId(item) {
      return item[this.itemIdField];
    },
    getColumnStyle(column) {
      if (!column.width) {
        return null;
      }

      if (typeof column.width === "number") {
        return { width: `${column.width}px` };
      }

      if (typeof column.width === "string") {
        return { width: column.width };
      }

      return null;
    },
    formatCellValue(item, column) {
      if (column.formatter) {
        return column.formatter(item[column.key], item);
      }
      return item[column.key];
    },
  },
};
</script>
